import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Link2, Link2Off, MinusCircle } from 'lucide-react';
import { cn } from '../../../lib/utils';
import { OrderHistory } from '../../../stores/appOrderHistoryStore';
import { CostPriceAnalysis } from '../../../services/costPrice/costPriceService';
import { WasteEntry } from '../../../services/waste/wasteService';
import { buildDishMatchIndex, matchWasteDish } from '../../../utils/reports/wasteByProduct';
import { productKey } from '../../../utils/reports/shared';
import { resolveProductIdentifier } from '../../../utils/platformUtils';
import { calculateUnitPrice } from '../../orderhistory/aggregations';

/** Pieces shared by the waste page's three views and its two link modals.
 *  `WASTE_REASON_LABELS` + `formatWasteQuantity` moved to `utils/reports/wasteByProduct.ts`
 *  when the utilization drawer started rendering the same rows — import them from there. */

/** One order-history product an ingredient entry can be linked to. */
export interface WasteProductOption {
  /** Canonical aggregation key — what the by-product view groups on. */
  key: string;
  platform: string;
  articleId?: string;
  productName: string;
  category?: string;
  /** Article id / resolved identifier, so a search can hit the product code too. */
  searchableId: string;
  stdUnit: string;
  unitPrice?: { value: number; unit: string };
  orderDate: Date;
}

/**
 * The product universe for ingredient links: one entry per canonical product key from
 * the restaurant's order history, latest purchase winning. Same dedup as the cost-price
 * ingredient picker (`SwapIngredientPanel`), but keyed with the report's `productKey` so
 * a link lands on exactly the key the by-product roll-up will group under.
 */
export function useWasteProductOptions(orders: OrderHistory[]): WasteProductOption[] {
  return useMemo(() => {
    const byKey = new Map<string, WasteProductOption>();
    for (const order of orders) {
      for (const product of order.productData) {
        const identifier = resolveProductIdentifier(order.platform, product.articleId, product.name);
        if (!identifier) continue;
        const key = productKey(order.platform, product.articleId, product.name);
        const orderDate = new Date(order.orderDate);
        const existing = byKey.get(key);
        if (existing && existing.orderDate >= orderDate) continue;
        const price = calculateUnitPrice(product.price ?? 0, product.quantityInfo, product.category);
        byKey.set(key, {
          key,
          platform: order.platform,
          articleId: product.articleId,
          productName: product.name,
          category: product.category,
          searchableId: identifier,
          stdUnit: product.quantityInfo?.stdUnit ?? '',
          unitPrice: price ? { value: price.value, unit: price.unit } : undefined,
          orderDate,
        });
      }
    }
    return Array.from(byKey.values()).sort((a, b) => a.productName.localeCompare(b.productName));
  }, [orders]);
}

/** Name → cost-price analysis, rebuilt only when the analyses change. The waste page's
 *  single source of dish identity: nothing about a dish link is stored. */
export function useDishMatchIndex(costPriceList: CostPriceAnalysis[]): Map<string, CostPriceAnalysis> {
  return useMemo(() => buildDishMatchIndex(costPriceList), [costPriceList]);
}

/** What an entry points at, for display: the matched analysis for a dish, the stored
 *  product snapshot for an ingredient. Null when it resolves to nothing. */
export function linkedLabel(entry: WasteEntry, dishIndex: Map<string, CostPriceAnalysis>): string | null {
  if (entry.wasteType === 'dish') {
    return matchWasteDish(entry.itemName, dishIndex)?.costPriceName ?? null;
  }
  if (entry.linkStatus !== 'linked') return null;
  return entry.linkedProductName;
}

interface BadgeProps {
  entry: WasteEntry;
  dishIndex: Map<string, CostPriceAnalysis>;
  className?: string;
}

/**
 * Per-row link state. A linked row names its target; an AI link that landed below full
 * confidence carries its % in an amber tint so a shaky match stays visible.
 */
export function WasteLinkBadge({ entry, dishIndex, className }: BadgeProps) {
  const { t } = useTranslation();
  const isDish = entry.wasteType === 'dish';
  const label = linkedLabel(entry, dishIndex);
  const base = 'inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium max-w-[220px]';

  // 'skipped' is an ingredient-only state — a dish is judged purely on whether its
  // cost price exists, so its badge never reports a stored status.
  if (!isDish && entry.linkStatus === 'skipped') {
    return (
      <span className={cn(base, 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-300', className)}>
        <MinusCircle className="w-3 h-3 shrink-0" />
        {t('waste.link.badgeSkipped')}
      </span>
    );
  }

  if (!label) {
    return (
      <span className={cn(base, 'bg-amber-50 text-amber-700 dark:bg-amber-900/20 dark:text-amber-300', className)}>
        <Link2Off className="w-3 h-3 shrink-0" />
        {/* A dish isn't "unlinked" — it has no cost price to link TO yet. */}
        {isDish ? t('waste.link.badgeNeedsSetup') : t('waste.link.badgeUnlinked')}
      </span>
    );
  }

  // Only a stored ingredient link can be uncertain; a derived dish match is exact.
  const uncertain =
    !isDish && entry.linkMethod === 'ai' && typeof entry.linkConfidence === 'number' && entry.linkConfidence < 1;
  return (
    <span
      className={cn(
        base,
        uncertain
          ? 'bg-amber-50 text-amber-700 dark:bg-amber-900/20 dark:text-amber-300'
          : 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300',
        className
      )}
      title={label}
    >
      <Link2 className="w-3 h-3 shrink-0" />
      <span className="truncate">{label}</span>
      {uncertain && (
        <span className="shrink-0">
          {t('waste.link.confidence', { pct: Math.round((entry.linkConfidence as number) * 100) })}
        </span>
      )}
    </span>
  );
}
